import { Injectable } from '@angular/core';
import { ExamdojoAuthService } from '@examdojo/auth';
import { ErrorHandlerService } from '@examdojo/core/error-handling';
import { distinctUntilArrayChanged } from '@examdojo/core/rxjs';
import { EntityTagRepository, Tag, TagRepository } from '@examdojo/tags';
import { EMPTY, map, Observable, tap } from 'rxjs';
import { QuestionBankQuery } from '../app/question-bank.query';
import { QuestionBankStore } from '../app/question-bank.store';
import { QuestionTagHttpService } from './question-tag-http.service';
import { UserTagHttpModel } from './user-tag-http.model';

function mapHttpModelToTag(httpModel: UserTagHttpModel): Tag {
  return {
    id: httpModel.id,
    name: httpModel.name,
    color: httpModel.color,
    emoji: httpModel.emoji,
    isSystem: httpModel.is_system,
  };
}

@Injectable()
export class QuestionBankTagsService implements TagRepository, EntityTagRepository<number> {
  constructor(
    private readonly questionTagHttpService: QuestionTagHttpService,
    private readonly authService: ExamdojoAuthService,
    private readonly questionBankQuery: QuestionBankQuery,
    private readonly questionBankStore: QuestionBankStore,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  private get currentUserId(): string {
    const userId = this.authService.currentUser?.id;
    if (!userId) {
      throw new Error('User not authenticated');
    }
    return userId;
  }

  load() {
    return this.questionTagHttpService.getAll(this.currentUserId).pipe(
      this.errorHandlerService.catchError('[QuestionBankTagsService]: Failed to load tags', () => EMPTY),
      map((tags) => tags.map(mapHttpModelToTag)),
      tap((tags) => {
        this.questionBankStore.updateProp('tags', tags);
      }),
    );
  }

  selectAll(): Observable<Tag[]> {
    return this.questionBankQuery.selectProp('tags');
  }

  create(tag: Omit<Tag, 'id' | 'isSystem'>): Observable<Tag> {
    return this.questionTagHttpService
      .create({
        name: tag.name,
        color: tag.color,
        emoji: tag.emoji ?? null,
        user_id: this.currentUserId,
      })
      .pipe(
        map(mapHttpModelToTag),
        tap((createdTag) => this.addTag(createdTag)),
      );
  }

  update(tag: Tag): Observable<Tag> {
    return this.questionTagHttpService
      .update(tag.id, {
        name: tag.name,
        color: tag.color,
        emoji: tag.emoji,
      })
      .pipe(
        map(mapHttpModelToTag),
        tap((updatedTag) => this.replaceTag(updatedTag)),
      );
  }

  delete(id: string): Observable<void> {
    return this.questionTagHttpService.delete(id).pipe(
      tap(() => this.removeTag(id)),
      map(() => void 0),
    );
  }

  selectForEntity(questionId: number): Observable<string[]> {
    return this.questionBankQuery.selectProp('questionTagMap').pipe(
      map((tagMap) => tagMap[questionId] ?? []),
      distinctUntilArrayChanged(),
    );
  }

  assign(questionId: number, tagId: string): Observable<void> {
    return this.questionTagHttpService.assignToQuestion(questionId, tagId, this.currentUserId).pipe(
      tap(() => this.addTagToQuestion(questionId, tagId)),
      map(() => void 0),
    );
  }

  remove(questionId: number, tagId: string): Observable<void> {
    return this.questionTagHttpService.removeTagFromQuestion(questionId, tagId, this.currentUserId).pipe(
      tap(() => this.removeTagFromQuestionStore(questionId, tagId)),
      map(() => void 0),
    );
  }

  private addTag(tag: Tag) {
    const currentTags = this.questionBankStore.getValue().props.tags;
    this.questionBankStore.updateProp('tags', [...currentTags, tag]);
  }

  private replaceTag(tag: Tag) {
    const currentTags = this.questionBankStore.getValue().props.tags;
    this.questionBankStore.updateProp(
      'tags',
      currentTags.map((t) => (t.id === tag.id ? tag : t)),
    );
  }

  private removeTag(tagId: string) {
    const currentTags = this.questionBankStore.getValue().props.tags;
    this.questionBankStore.updateProp(
      'tags',
      currentTags.filter((t) => t.id !== tagId),
    );
  }

  private addTagToQuestion(questionId: number, tagId: string): void {
    const currentMap = this.questionBankStore.getValue().props.questionTagMap;
    const currentTags = currentMap[questionId] ?? [];
    if (!currentTags.includes(tagId)) {
      this.questionBankStore.updateProp('questionTagMap', {
        ...currentMap,
        [questionId]: [...currentTags, tagId],
      });
    }
  }

  private removeTagFromQuestionStore(questionId: number, tagId: string): void {
    const currentMap = this.questionBankStore.getValue().props.questionTagMap;
    const currentTags = currentMap[questionId] ?? [];
    this.questionBankStore.updateProp('questionTagMap', {
      ...currentMap,
      [questionId]: currentTags.filter((id: string) => id !== tagId),
    });
  }
}
